import i18n, { t } from 'i18next'

import { LogoStore } from '~/components/LogoStore'
import { ConversionRateCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionRateCutoffAlertIcon'
import { ConversionsCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionsCutoffAlertIcon'
import { RoasMetricCell } from '~/modules/retailMedia/components/RoasMetricCell'
import { formatRoasCutoffDate } from '~/modules/retailMedia/constants'

import { MetricTooltip } from '../../MetricMethodologyTooltip'

export const schema = (
  showClickAlert = false
): TableSchema<SubPublisherWithMetrics> => ({
  publisher: {
    title: 'Publisher',
    sortKey: 'name',
    render: rowData => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <LogoStore
          alt={rowData.title}
          linkLogo={rowData.logo_url}
          backgroundLogo={rowData.account_theme?.header_color || 'transparent'}
          size="medium"
        />
        <div style={{ lineHeight: 1, marginLeft: '.5rem' }}>
          <strong className="title">{rowData.title}</strong>
        </div>
      </div>
    ),
    className: 'order__details col__infos col__title',
    showTotal: true
  },
  requests: {
    sortKey: 'requests',
    sortInitialToggleDirection: 'DESC',
    title: t('rm:Request'),
    render: ({ metrics }) => metrics.requests
  },
  impressions: {
    sortKey: 'impressions',
    sortInitialToggleDirection: 'DESC',
    title: t('rm:metrics.impressions'),
    helpText: t('rm:metrics.impressions.tooltip'),
    render: ({ metrics }) => metrics.impressions
  },
  views: {
    sortKey: 'views',
    sortInitialToggleDirection: 'DESC',
    title: t('rm:metrics.views'),
    helpText: t('rm:metrics.views.tooltip'),
    render: ({ metrics }) => metrics.views
  },
  clicks: {
    sortKey: 'clicks',
    sortInitialToggleDirection: 'DESC',
    title: t('rm:metrics.clicks'),
    helpText: t('rm:metrics.clicks.tooltip'),
    render: ({ metrics }) => metrics.clicks
  },
  ctr: {
    title: t('rm:metrics.ctr'),
    helpContent: <MetricTooltip metric="ctr" />,
    sortKey: 'ctr',
    sortInitialToggleDirection: 'DESC',
    render: ({ metrics }) => metrics.ctr
  },
  totalSpent: {
    sortKey: 'total_spent',
    sortInitialToggleDirection: 'DESC',
    title: t('rm:metrics.adsRevenue'),
    helpText: t('rm:metrics.cost.tooltip'),
    render: ({ metrics }) => metrics.totalSpent
  },
  conversion: {
    sortKey: 'conversions',
    sortInitialToggleDirection: 'DESC',
    title: t('rm:metrics.conversions'),
    headerSuffix: showClickAlert ? <ConversionsCutoffAlertIcon /> : undefined,
    helpText: t('rm:metrics.conversions.tooltip'),
    render: ({ metrics }) => metrics.conversions
  },
  conversion_rate: {
    sortKey: 'conversion_rate',
    sortInitialToggleDirection: 'DESC',
    title: t('rm:metrics.conversion_rate'),
    headerSuffix: showClickAlert ? (
      <ConversionRateCutoffAlertIcon />
    ) : undefined,
    helpContent: (
      <MetricTooltip
        metric="conversion_rate"
        values={{ date: formatRoasCutoffDate(i18n.language) }}
        learnMoreHref={t('rm:alerts.conversionUpdate.link')}
      />
    ),
    render: ({ metrics }) => metrics.conversionRate
  },
  income: {
    sortKey: 'income',
    sortInitialToggleDirection: 'DESC',
    title: t('rm:metrics.sales'),
    helpText: t('rm:metrics.sales.tooltip'),
    render: ({ metrics }) => metrics.income
  },
  roas: {
    title: t('rm:metrics.roas'),
    helpContent: (
      <MetricTooltip
        metric="roas"
        values={{ date: formatRoasCutoffDate(i18n.language) }}
        learnMoreHref={t('rm:alerts.roasUpdate.link')}
      />
    ),
    sortKey: 'roas',
    sortInitialToggleDirection: 'DESC',
    render: ({ metrics }) => (
      <RoasMetricCell metric="roas" value={metrics.roas} raw={metrics.raw} />
    )
  }
})
